// CCCallbackScreen — handles the OAuth return from Constant Contact
// (P-047 / D-142). Renders at `#/auth/cc-callback?code=...&state=...`
// after CC redirects the browser back from its authorize page.
//
// On mount it dynamic-imports `cc-oauth` (matches the LoginScreen +
// Sign-out lazy-load pattern at D-140 — keeps the V2.2 OAuth chunk out
// of the initial bundle), runs `handleCallback()`, and on success
// navigates to `#/settings` where the user sees the now-Connected
// state. On failure it surfaces the error inline with a "Try again"
// link that re-runs `startOAuth()`; the page stays mounted so the user
// has a stable surface to read the error (vs a toast that disappears).
//
// Effect-fires-once guard: a strict-mode-friendly ref keeps the effect
// from double-firing in dev (where Preact strict mode mounts → unmounts
// → re-mounts effects). Production runs through the effect exactly
// once anyway; the guard is for dev-mode parity.

import { useEffect, useState } from 'preact/hooks'

type Status =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }

export function CCCallbackScreen() {
  const [status, setStatus] = useState<Status>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const { handleCallback } = await import('../../lib/cc-oauth')
        await handleCallback()
        if (!cancelled) window.location.hash = '#/settings'
      } catch (err) {
        if (cancelled) return
        const message =
          err instanceof Error
            ? err.message
            : "Couldn't connect to Constant Contact. Please try again."
        setStatus({ kind: 'error', message })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleRetry = async () => {
    try {
      const { startOAuth } = await import('../../lib/cc-oauth')
      await startOAuth()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't restart the Constant Contact connection."
      setStatus({ kind: 'error', message })
    }
  }

  return (
    <div class="mx-auto max-w-md px-4 py-12">
      <section
        aria-labelledby="cc-callback-heading"
        class="rounded border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h1 id="cc-callback-heading" class="m-0 mb-3 text-lg font-semibold text-gray-900">
          {status.kind === 'loading' ? 'Connecting to Constant Contact…' : 'Connection failed'}
        </h1>
        {status.kind === 'loading' ? (
          <p class="m-0 text-sm text-gray-600">
            Finishing the handshake with Constant Contact — this usually takes a second or two.
          </p>
        ) : (
          <>
            <p role="alert" class="m-0 mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              {status.message}
            </p>
            <div class="flex items-center gap-3 text-sm">
              <button
                type="button"
                onClick={handleRetry}
                class="rounded bg-ypo-navy px-3 py-1.5 font-medium text-white hover:bg-ypo-navy/90"
              >
                Try connecting again
              </button>
              <a href="#/settings" class="text-ypo-navy hover:underline">
                Back to Settings
              </a>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
